import { Component, Inject, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MasterService } from "@frontend/app/serivces/master.service";
import { GlobalService } from "@frontend/app/serivces/global.service";
import { forkJoin, map } from "rxjs";
import { SanitizerPipe } from "@frontend/app/pipes/sanitizer.pipe";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { trigger, state, style, transition, animate } from "@angular/animations";

interface MenuItem {
  MenuID: any;
  MenuName: string;
  RouterPath: string;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  expanded?: boolean;
  ChildList?: MenuItem[];
  MenuWithPermissions?: any[];
  parentItem?: MenuItem | null; // Optional parent reference for propagation
}

@Component({
  selector: "app-user-role-permissions-dialog",
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, SanitizerPipe],
  templateUrl: "./user-role-permissions-dialog.component.html",
  styleUrl: "./user-role-permissions-dialog.component.scss",
  animations: [
    trigger("indicatorRotate", [
      state("collapsed", style({ transform: "rotate(270deg)" })),
      state("expanded", style({ transform: "rotate(360deg)" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4,0.0,0.2,1)")),
    ]),
  ],
})
export class UserRolePermissionsDialogComponent implements OnInit {
  private masterService = inject(MasterService);
  private globalService = inject(GlobalService);
  readonly SVG_ICONS = SVG_ICONS;

  userName: string = "";
  masterMenuList: MenuItem[] = [];
  isLoading = true;

  constructor(
    public dialogRef: MatDialogRef<UserRolePermissionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.userName = data.user.UserLoginID;
  }

  ngOnInit() {
    this.loadUserPermissions();
  }

  loadUserPermissions() {
    const roleIds = this.data.user.UserTypeIDs;
    if (!roleIds || roleIds.length === 0) {
      this.masterMenuList = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    
    // Fetch permissions for all roles assigned to the user
    const requests = roleIds.map((id: number) => this.masterService.GetUserTypeByID(id));

    forkJoin<any[]>(requests).subscribe({
      next: (results: any[]) => {
        // Merge all menu tables from different roles
        const mergedTable = this.mergeRolePermissions(results);
        const processedMenu = this.globalService.menuSetter(mergedTable);
        this.initializeMenuPermissions(processedMenu);
        
        // Filter to only show modules with any rights
        this.masterMenuList = this.filterMenuByRights(processedMenu);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private mergeRolePermissions(results: any[]): any[] {
    const menuMap = new Map<number, any>();

    results.forEach(res => {
      const table = res.Table || [];
      table.forEach((item: any) => {
        if (!menuMap.has(item.MenuID)) {
          // Clone the item to avoid modifying the original
          menuMap.set(item.MenuID, JSON.parse(JSON.stringify(item)));
        } else {
          // Merge permissions if already exists
          const existingItem = menuMap.get(item.MenuID);
          const existingPerms = existingItem.MenuWithPermissions?.[0] || {};
          const nextPerms = item.MenuWithPermissions?.[0] || {};

          if (existingItem.MenuWithPermissions) {
            existingItem.MenuWithPermissions[0] = {
              ...existingPerms,
              CanAdd: existingPerms.CanAdd || nextPerms.CanAdd,
              CanEdit: existingPerms.CanEdit || nextPerms.CanEdit,
              CanDelete: existingPerms.CanDelete || nextPerms.CanDelete,
              CanView: existingPerms.CanView || nextPerms.CanView,
            };
          }
        }
      });
    });

    return Array.from(menuMap.values());
  }

  private initializeMenuPermissions(menus: MenuItem[], parent: MenuItem | null = null) {
    menus.forEach(menu => {
      const permissions = menu.MenuWithPermissions?.[0];
      menu.canAdd = permissions?.CanAdd ?? false;
      menu.canEdit = permissions?.CanEdit ?? false;
      menu.canDelete = permissions?.CanDelete ?? false;
      menu.canView = permissions?.CanView ?? false;
      menu.expanded = true; // Auto-expand to show children like Settings
      menu['parentItem'] = parent; // Store parent reference for upward propagation
      
      if (menu.ChildList?.length) {
        this.initializeMenuPermissions(menu.ChildList, menu);
      }
    });
  }

  private filterMenuByRights(menus: MenuItem[]): MenuItem[] {
    return menus.filter(menu => {
      const hasDirectRights = menu.canAdd || menu.canEdit || menu.canDelete || menu.canView;
      
      if (menu.ChildList && menu.ChildList.length > 0) {
        menu.ChildList = this.filterMenuByRights(menu.ChildList);
      }

      const hasChildRights = menu.ChildList && menu.ChildList.length > 0;
      
      return hasDirectRights || hasChildRights;
    });
  }

  togglePermission(item: MenuItem, type: 'add' | 'edit' | 'delete', parent: MenuItem | null) {
    let newValue: boolean;
    if (type === 'add') {
      item.canAdd = !item.canAdd;
      newValue = item.canAdd;
    } else if (type === 'edit') {
      item.canEdit = !item.canEdit;
      newValue = item.canEdit;
    } else {
      item.canDelete = !item.canDelete;
      newValue = item.canDelete;
    }

    // Propagate change down to all children
    if (item.ChildList?.length) {
      this.propagatePermissionDown(item.ChildList, type, newValue);
    }

    // Propagate selection up to all parents
    if (newValue) {
      this.propagatePermissionUp(item.parentItem, type);
    }
  }

  private propagatePermissionDown(list: MenuItem[], type: 'add' | 'edit' | 'delete', value: boolean) {
    list.forEach(child => {
      if (type === 'add') child.canAdd = value;
      else if (type === 'edit') child.canEdit = value;
      else child.canDelete = value;
      
      if (child.ChildList?.length) {
        this.propagatePermissionDown(child.ChildList, type, value);
      }
    });
  }

  private propagatePermissionUp(parent: MenuItem | null | undefined, type: 'add' | 'edit' | 'delete') {
    if (!parent) return;

    if (type === 'add') parent.canAdd = true;
    else if (type === 'edit') parent.canEdit = true;
    else parent.canDelete = true;

    // Recurse up to the root
    if (parent.parentItem) {
      this.propagatePermissionUp(parent.parentItem, type);
    }
  }

  toggleMenuItem(item: MenuItem) {
    item.expanded = !item.expanded;
  }

  onClose() {
    this.dialogRef.close();
  }
}
